/**
 * One tap on the map while a placement is pending — a new route, a route's new
 * position, or a spray wall's spot (docs/spec/11-map-routes.md, "Handling
 * placement save errors").
 *
 * Placement ends AT the tap, before the write is even sent — not when the server
 * acknowledges it. A Firestore client write is applied locally at once (the
 * routes listener shows the new marker), but its acknowledgement takes as long as
 * the gym's network: seconds on a weak wifi, never while offline. Waiting for it
 * kept the "tap to place" banner up after the marker had landed, the map's mode
 * switches stayed blocked, and every further tap wrote another route.
 *
 * Pure: the caller hands in the write and the reactions, so the order is testable
 * without the whole map hook (`__tests__/mapPlacement.test.js`).
 *
 * @param {object}   p
 * @param {object}   p.placing       the pending placement (nothing happens without one)
 * @param {Function} p.endPlacement  leaves placement mode (`setPending(null)`)
 * @param {Function} p.write         (placing) → the write's promise
 * @param {Function} [p.onSaved]     (placing) after the server acknowledged it
 * @param {Function} p.onFailed      (placing, error) when the write failed
 * @returns {Promise<boolean>} whether the write went through
 */
export async function commitPlacement({ placing, endPlacement, write, onSaved, onFailed }) {
  if (!placing) return false;
  endPlacement();
  try {
    await write(placing);
  } catch (e) {
    onFailed(placing, e);
    return false;
  }
  // Outside the try: a throw here is not a failed save.
  onSaved?.(placing);
  return true;
}
